"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useSidebarStore } from "@/store/sidebar.store";
import { useAuthStore } from "@/store/auth.store";
import { authService } from "@/services/auth.service";
import { walletService } from "@/services/wallet.service";
import { useOngoingOrders } from "@/hooks/use-orders";
import { useTranslation } from "@/lib/i18n";
import { LanguageToggle } from "@/components/layout/language-toggle";
import { BadgePercent, CircleUser, ClipboardList, Command, Heart, House, LogOut, MapPin, ShieldCheck, ShoppingCart, Wallet, WalletMinimal } from "lucide-react";
import { loginHref } from "@/lib/next-path";

const NAV_ITEMS = [
  { key: "home" as const, href: "/home", icon: "home" },
  { key: "grocery" as const, href: "/grocery", icon: "grocery" },
  { key: "browseAll" as const, href: "/browse", icon: "browse" },
  { key: "gifts" as const, href: "/gifts", icon: "gifts" },
];

const ICONS: Record<string, React.ReactNode> = {
  home: (
<House className="h-5 w-5" />
  ),
  grocery: (
<ShoppingCart className="h-5 w-5" />
  ),
  browse: (
<Command className="h-5 w-5" />
  ),
  gifts: (
<BadgePercent className="h-5 w-5" />
  ),
  orders: (
<ClipboardList className="h-5 w-5" />
  ),
  account: (
<CircleUser className="h-5 w-5" />  
  ),
};

export function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, clearAuth } = useAuthStore();
  const t = useTranslation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const { collapsed } = useSidebarStore();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  const { data: ongoingOrders } = useOngoingOrders();
  const ongoingCount = (() => {
    const raw = ongoingOrders as unknown[] | { items?: unknown[] } | undefined;
    const list: unknown[] = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
    return list.filter((o) => {
      const x = o as { ulid?: string; created_at?: string };
      return !!x?.ulid && !!x?.created_at && !Number.isNaN(new Date(x.created_at).getTime());
    }).length;
  })();

  const { data: walletBalance } = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: () => walletService.getBalance(),
    enabled: !!user,
  });

  const isStorePage = /^\/stores\/[^/]+/.test(pathname);
  const isIconOnly = isStorePage ? !collapsed : collapsed;

  // Track last non-store page so store detail back button can return here
  useEffect(() => {
    if (!pathname.startsWith("/stores/")) {
      sessionStorage.setItem("last-non-store-page", pathname);
    }
  }, [pathname]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        (!popupRef.current || !popupRef.current.contains(target))
      ) {
        setAccountOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authService.logout();
    } catch {
      // proceed regardless
    }
    clearAuth();
    setAccountOpen(false);
    router.push("/");
    // Reset the view to the top after logging out (login page or /home).
    requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0 }));
  };

  // Detect if cart panel is open (by checking body class or a global state, fallback to window.cartPanelOpen if set by CartPanel)
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  useEffect(() => {
    const handler = () => {
      setCartPanelOpen(document.body.classList.contains("cart-panel-open"));
    };
    handler();
    window.addEventListener("cart-panel-toggle", handler);
    return () => window.removeEventListener("cart-panel-toggle", handler);
  }, []);

  return (
    <aside className={`hidden lg:flex flex-col border-r border-gray-200 bg-white dark:bg-[#1E1E1E] dark:border-[#313131] h-[calc(100vh-64px)] sticky top-16 shrink-0 z-40 transition-all duration-200 ${
      isIconOnly ? "w-[60px]" : "w-56"
    }${cartPanelOpen ? " sidebar-sombre" : ""}`}>
      {/* Hamburger removed — now in header */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <ul className="space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname === item.href;
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  title={t.nav[item.key]}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "font-semibold dark:bg-[#313131] dark:text-white bg-gray-100 text-gray-900"
                      : "text-gray-700 hover:bg-gray-50  dark:text-gray-400 dark:hover:bg-[#313131]"
                  } ${isIconOnly ? "justify-center" : ""}`}
                >
                  {ICONS[item.icon]}
                  {!isIconOnly && t.nav[item.key]}
                </Link>
              </li>
            );
          })}

          {/* Separator */}
          <li className="py-1.5 px-1" aria-hidden>
            <hr className="border-gray-200 dark:border-[#313131]" />
          </li>

          {/* Orders — only when logged in */}
          {user && <li>
            <Link
              href="/orders"
              title={t.nav.orders}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                pathname === "/orders"
                  ? "font-semibold dark:bg-[#313131] dark:text-white bg-gray-100 text-gray-900"
                  : "text-gray-700 hover:bg-gray-50  dark:text-gray-400 dark:hover:bg-[#313131]"
              } ${isIconOnly ? "justify-center" : ""}`}
            >
              <span className="relative shrink-0">
                {ICONS.orders}
                {ongoingCount > 0 && collapsed && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white leading-none">
                    {ongoingCount > 9 ? "9+" : ongoingCount}
                  </span>
                )}
              </span>
              {!isIconOnly && (
                <span className="flex flex-1 items-center justify-between">
                  {t.nav.orders}
                  {ongoingCount > 0 && (
                    <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
                      {ongoingCount > 9 ? "9+" : ongoingCount}
                    </span>
                  )}
                </span>
              )}
            </Link>
          </li>}

          {/* Sign in link — only when logged out */}
          {!user && (
            <li>
              <Link
                href={loginHref(pathname)}
                title={t.sidebar.signIn}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition text-gray-700 hover:bg-gray-50  dark:text-gray-400 dark:hover:bg-[#313131] ${isIconOnly ? "justify-center" : ""}`}
              >
                {ICONS.account}
                {!isIconOnly && t.sidebar.signIn}
              </Link>
            </li>
          )}

          {/* Account — opens popup (only when logged in) */}
          {user && <li>
            {(() => {
              // Consider account active if on any account-related page
              const accountActive = [
                "/account/profile",
                "/account/settings",
                "/account/wallet",
                "/account/addresses",
                "/saved-stores"
              ].includes(pathname);
              return (
                <button
                  ref={buttonRef}
                  onClick={() => {
                    if (buttonRef.current) {
                      const rect = buttonRef.current.getBoundingClientRect();
                      setPopupPos({ top: rect.top, left: rect.right + 8 });
                    }
                    setAccountOpen((o) => !o);
                  }}
                  title={t.nav.account}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    accountActive
                      ? "font-semibold dark:bg-[#313131] dark:text-white bg-gray-100 text-gray-900"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#313131]"
                  } ${isIconOnly ? "justify-center" : ""}`}
                >
                  {ICONS.account}
                  {!isIconOnly && t.nav.account}
                </button>
              );
            })()}

            {/* Account popup — portal to escape overflow clipping */}
            {accountOpen && popupPos && typeof window !== "undefined" && createPortal(
              <div
                ref={popupRef}
                style={{ top: popupPos.top, left: popupPos.left }}
                className="fixed w-72 rounded-xl border border-gray-200 bg-white dark:bg-[#2A2A2A] dark:border-[#313131] shadow-2xl z-[200] overflow-hidden">

                {/* User header */}
                <div className="px-4 py-4 border-b border-gray-200 dark:border-[#313131]">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-800 text-white text-sm font-bold uppercase">
                      {user?.name ? user.name.slice(0, 1) : "?"}
                    </div>
                    <div className="min-w-0 flex-1">
                    <Link href="/account/profile" onClick={() => setAccountOpen(false)} className="text-xs font-medium text-secondary dark:text-primary hover:underline">                   
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.name ?? "—"}</p>
                       </Link>
                      {user?.phone && <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 truncate">{user.phone}</p>}
                      {user?.email && <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 truncate">{user.email}</p>}
                    </div>
                  </div>

                </div>

                {/* Nav links */}
                <div className="border-b border-gray-200 dark:border-[#313131]">
                  <Link
                    href="/account"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition border-b border-gray-100 dark:border-[#313131]"
                  >
                    <CircleUser className="h-4 w-4" />
                    {t.accountPopup.myAccount}
                  </Link>
                  <Link
                    href="/account/addresses"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition border-b border-gray-100 dark:border-[#313131]"
                  >
                    <MapPin className="h-4 w-4" />
                    {t.accountPopup.addresses}
                  </Link>
                  <Link
                    href="/saved-stores"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition border-b border-gray-100 dark:border-[#313131]"
                  >
                    <Heart className="h-4 w-4" /> 
                    {t.accountPopup.savedStores}
                  </Link>
                  <Link
                    href="/account/wallet"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition"
                  >
                    <Wallet className="h-4 w-4" />
                    <span className="flex-1">{t.accountPopup.wallet}</span>
                    {walletBalance?.balance != null && (
                      <span className="text-xs font-semibold text-secondary dark:text-primary">
                        {Number(walletBalance.balance).toLocaleString("fr-DZ", { minimumFractionDigits: 2 })} {walletBalance.currency ?? "DA"}
                      </span>
                    )}
                  </Link>
                  <Link
                    href="/account/enterprise"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition border-b border-gray-100 dark:border-[#313131]"
                  >
                     <WalletMinimal className="h-4 w-4" />
                    
                    {t.accountPopup.enterprise}
                  </Link>
                  <Link
                    href="/account/subscription"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#313131] transition"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {t.accountPopup.subscription}
                  </Link>
                </div>

                {/* About */}
                <div className="border-b border-gray-200 dark:border-[#313131] py-1">
                  <Link
                    href="/account/about"
                    onClick={() => setAccountOpen(false)}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:bg-gray-50  dark:hover:bg-[#313131] transition"
                  >
                    {t.accountPopup.about}
                  </Link>
                  <a
                    href="https://help.fastdelivery.dz"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setAccountOpen(false)}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 hover:bg-gray-50  dark:hover:bg-[#313131] transition"
                  >
                    {t.accountPopup.helpSupport}
                  </a>
                </div>
              </div>,
              document.body
            )}
          </li>}
        </ul>
      </nav>

      {/* Language switch + sign out — always reachable, no menu needed */}
      <div className="shrink-0 border-t border-gray-200 dark:border-[#313131] px-2 py-2 space-y-1">
        {isIconOnly ? (
          <div className="flex justify-center">
            <LanguageToggle compact />
          </div>
        ) : (
          <div className="px-1 pb-1">
            <LanguageToggle className="w-full justify-center" />
          </div>
        )}
        {user && (
          <button
            onClick={() => setLogoutConfirmOpen(true)}
            disabled={isLoggingOut}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition disabled:opacity-50 ${isIconOnly ? "justify-center" : ""}`}
          >
            <LogOut className="h-5 w-5 text-gray-500 dark:text-gray-400 dark:text-gray-500 shrink-0" />
            {!isIconOnly && (isLoggingOut ? t.sidebar.signingOut : t.sidebar.signOut)}
          </button>
        )}
      </div>

      {/* Logout confirmation modal */}
      {logoutConfirmOpen && typeof window !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#2A2A2A] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center px-6 pt-6 pb-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100  mb-3">
                <LogOut className="h-6 w-6 text-gray-600 dark:text-gray-400 dark:text-gray-400" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{t.sidebar.logoutTitle}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">{t.sidebar.logoutConfirm}</p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setLogoutConfirmOpen(false)}
                className="flex-1 rounded-xl border border-gray-200 dark:border-[#313131] py-3 text-sm font-medium text-gray-700 dark:text-gray-400 hover:bg-gray-50  dark:hover:bg-[#313131] transition"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={() => { setLogoutConfirmOpen(false); handleLogout(); }}
                disabled={isLoggingOut}
                className="flex-1 rounded-xl bg-red-600 py-3 text-sm font-bold text-white hover:bg-[#022d17] transition disabled:opacity-50"
              >
                {isLoggingOut ? t.sidebar.signingOut : t.common.confirm}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </aside>
  );
}
